import { initializeTable } from '../tables';
import RowContextMenuButtonColumn from '../formatters/RowContextMenuButtonColumn';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import toggleNestedColumn from '../formatters/ToggleNestedColumn';
import {
    getCookie,
    sendDeleteRequest,
    sendPatchRequest,
    debounce,
    hasPermission,
} from '../../utils';
import { addAlert } from '../../alerts';
import { inputPrompt, loaderPrompt } from '../../prompts';

function getManualsColumns(stationType) {
    const baseStartColumns = [
        {
            resizable: false,
            width: 60,
            headerSort: false,
            formatter: toggleNestedColumn,
        },
        { title: '#', field: 'id', width: 60 },
    ];

    const baseEndColumns = [
        {
            title: gettext('Akcja'),
            resizable: false,
            headerSort: false,
            width: 100,
            formatter: RowContextMenuButtonColumn,
        },
    ];

    let dynamicColumns = [];
    if (stationType === 'DD') {
        // Device Disassembly Columns
        dynamicColumns = [
            {
                title: gettext('Grupa urządzenia'),
                field: 'device_group',
                resizable: false,
                formatter: function (cell) {
                    const rowData = cell.getRow().getData();
                    if (rowData.is_universal) {
                        return `<i class="font-italic">${gettext('Instrukcja Uniwersalna')}</i>`;
                    }
                    return cell.getValue();
                },
            },
            {
                title: gettext('Typ urządzenia'),
                field: 'device_type',
                resizable: false,
            },
            {
                title: gettext('Producent'),
                field: 'device_manufacturer',
                resizable: false,
            },
            {
                title: gettext('Model'),
                field: 'device_models',
                resizable: false,
                formatter: function (cell, formatterParams, onRendered) {
                    const models = cell.getValue();
                    if (!models) return '';
                    return models.replace(/\n/g, '<br>');
                },
            },
        ];
    } else {
        // Component Related Columns (CP, CT, CB)
        dynamicColumns = [
            {
                title: gettext('Grupa podzespołu'),
                field: 'component_group',
                resizable: false,
                formatter: function (cell) {
                    const rowData = cell.getRow().getData();
                    if (rowData.is_universal) {
                        return `<i class="font-italic">${gettext('Instrukcja Uniwersalna')}</i>`;
                    }
                    return cell.getValue();
                },
            },
            {
                title: gettext('Typ podzespołu'),
                field: 'component_type',
                resizable: false,
            },
        ];
    }

    return [...baseStartColumns, ...dynamicColumns, ...baseEndColumns];
}

function hasExecutionPermission(stationType) {
    const permissionMap = {
        DD: 'users.execute_device_disassembly',
        CP: 'users.execute_component_preparation',
        CT: 'users.execute_component_testing',
        CB: 'users.execute_component_beauty',
    };
    const permission = permissionMap[stationType];
    return permission && hasPermission(permission);
}

function startManualExecution(rowData) {
    inputPrompt({
        options: {
            title: gettext('Wprowadź ID urządzenia/podzespołu'),
            confirmText: gettext('Rozpocznij proces'),
            confirmAction: ({ value }) => {
                loaderPrompt('Trwa wczytywanie... Proszę czekać.');
                window.location.href = `/executions/start/${rowData.id}/${value}/`;
            },
        },
    });
}

export default function initializeManualsTable({
    handleFiltering = true,
    ...options
}) {
    const rootElement = document.querySelector('#manualsTable');
    const filtersElement = document.querySelector('#manualsFilters');
    const tabsElement = document.querySelector('.table-tabs');
    const ajaxURL = rootElement.dataset.ajaxUrl;
    const pillsContainerSelector = '.active-filters-container';

    const initialActiveTab = tabsElement.querySelector('.tab.active');
    const initialStationType = initialActiveTab
        ? initialActiveTab.dataset.value
        : 'DD';
    const initialColumns = getManualsColumns(initialStationType);

    const table = initializeTable({
        tableElement: rootElement,
        filtersElement: filtersElement,
        tabsElement: tabsElement,
        ajaxURL: ajaxURL,
        columns: initialColumns,
        rowContextMenu: function (e, row) {
            const rowData = row.getData();
            const canExecute = hasExecutionPermission(rowData.station_type);
            const menu = [
                {
                    label: gettext('Edytuj'),
                    disabled:
                        rowData.is_archived ||
                        !hasPermission('users.manage_manuals'),
                    action: function (e, row) {
                        window.location.href = rowData.update_url;
                    },
                },
                {
                    label: gettext('Rozpocznij proces'),
                    disabled:
                        !rowData.versions.some((obj) => obj.status === 1) ||
                        rowData.is_archived ||
                        !canExecute,
                    action: function (e, row) {
                        startManualExecution(rowData);
                    },
                },
                {
                    label: rowData.is_archived
                        ? gettext('Przywróć')
                        : gettext('Archiwizuj'),
                    disabled: !hasPermission('users.manage_manuals'),
                    action: async function (e, row) {
                        const response = await sendPatchRequest(
                            `/api/manuals/${rowData.id}`,
                            {
                                is_archived: !rowData.is_archived,
                            },
                        );
                        const data = await response.json();
                        if (response.ok) {
                            rowData.is_archived = !rowData.is_archived;
                            row.delete();
                            if (rowData.is_archived) {
                                addAlert(
                                    'success',
                                    gettext(
                                        'Pomyślnie zarchiwizowano instrukcje.',
                                    ),
                                );
                            } else {
                                addAlert(
                                    'success',
                                    gettext(
                                        'Pomyślnie przywrócono instrukcje.',
                                    ),
                                );
                            }
                        } else {
                            addAlert('error', data.message);
                        }
                    },
                },
                {
                    label: gettext('Usuń'),
                    disabled:
                        !rowData.versions.every((obj) => obj.status === 0) ||
                        !hasPermission('users.manage_manuals'),
                    action: async function (e, row) {
                        const response = await sendDeleteRequest(
                            `/api/manuals/${rowData.id}`,
                        );
                        const data = await response.json();
                        if (response.ok) {
                            addAlert('success', data.message);
                            row.delete();
                        } else {
                            addAlert('error', data.message);
                        }
                    },
                },
            ];
            const filteredMenu = menu.filter((item) => !item.disabled);
            if (filteredMenu.length) return filteredMenu;
            return [
                {
                    label: gettext('Brak uprawnień do wykonania akcji.'),
                    disabled: true,
                },
            ];
        },
        rowFormatter: (row) => {
            const containerElement = document.createElement('div');
            const nestedTableElement = document.createElement('div');
            containerElement.classList.add('nested-table-container');
            containerElement.classList.add('hidden');
            nestedTableElement.classList.add('nested-table');

            containerElement.appendChild(nestedTableElement);
            row.getElement().appendChild(containerElement);

            new Tabulator(nestedTableElement, {
                layout: 'fitColumns',
                data: row.getData().versions,
                selectableRows: false,
                rowHeight: 40,
                rowContextMenu: function (e, row) {
                    const rowData = row.getData();
                    return [
                        {
                            label: gettext('Podgląd wersji'),
                            action: function (e, row) {
                                window.location.href = `/manuals/preview/version/${rowData.id}/device-specification/`;
                            },
                        },
                        {
                            label: gettext('Duplikuj'),
                            disabled: !hasPermission('users.manage_manuals'),
                            action: async function (e, row) {
                                const response = await fetch(
                                    `/api/manuals/versions/${rowData.id}/duplicate`,
                                    {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'X-CSRFToken':
                                                getCookie('csrftoken'),
                                        },
                                    },
                                );
                                const data = await response.json();
                                if (response.ok) {
                                    window.location.href = data.update_url;
                                } else {
                                    addAlert('error', data.message);
                                }
                            },
                        },
                    ];
                },
                columns: [
                    {
                        title: gettext('Numer wersji'),
                        field: 'version_number',
                        resizable: false,
                        headerSort: false,
                    },
                    {
                        title: gettext('Data'),
                        field: 'publication_time',
                        formatter: 'datetime',
                        formatterParams: {
                            inputFormat: 'iso',
                            outputFormat: 'dd-MM-yyyy',
                        },
                        resizable: false,
                        headerSort: false,
                        sorter: 'datetime',
                        sorterParams: {
                            format: 'iso',
                            alignEmptyValues: 'top',
                        },
                    },
                    {
                        title: gettext('Autor instrukcji'),
                        field: 'author_name',
                        resizable: false,
                        headerSort: false,
                    },
                    {
                        title: gettext('Status'),
                        field: 'status_name',
                        resizable: false,
                        headerSort: false,
                    },
                    {
                        title: gettext('Akcja'),
                        resizable: false,
                        headerSort: false,
                        width: 100,
                        formatter: RowContextMenuButtonColumn,
                    },
                ],
            });
        },
        initialFilter: [
            {
                field: 'is_archived',
                type: '=',
                value: false,
            },
        ],
        handleFiltering: handleFiltering,
        pillsConfig: {
            containerSelector: pillsContainerSelector,
            ignoreFields: ['station_type'],
            specialPills: {
                is_archived: {
                    valueToMatch: 'True',
                    labelText: gettext('Status: Archiwalne'),
                    filterInputId: 'id_is_archived',
                },
            },
        },
        ...options,
    });

    return table;
}

export function setupManualsPageInteractions() {
    const TABS_STORAGE_KEY = 'lastActiveManualTab';
    const tabsContainer = document.querySelector('.table-tabs');
    const allTabs = tabsContainer.querySelectorAll('.tab');
    const newManualLabel = document.getElementById('newManualLabel');
    const newManualBtn = document.getElementById('newManualBtn');
    const stationTypeInput = document.getElementById('id_station_type');
    const isArchivedField = document.getElementById('id_is_archived');
    const filtersForm = document.querySelector('#manualsFilters .form-fields');
    const deviceFilterNames = [
        'device_manufacturer',
        'device_models',
        'device_type',
        'device_group',
    ];
    const componentFilterNames = ['component_type', 'component_group'];

    if (hasPermission('users.manage_manuals')) {
        newManualBtn.classList.remove('hidden');
    }

    function getFilterFieldWrapper(fieldName) {
        const fieldElement = document.getElementById(`id_${fieldName}`);
        return fieldElement
            ? fieldElement.closest('.form-field') || fieldElement.parentElement
            : null;
    }

    function updateFilterVisibility(activeTabValue) {
        const isDisassemblyTab = activeTabValue === 'DD';
        deviceFilterNames.forEach((name) => {
            const wrapper = getFilterFieldWrapper(name);
            if (wrapper) wrapper.style.display = isDisassemblyTab ? '' : 'none';
        });
        componentFilterNames.forEach((name) => {
            const wrapper = getFilterFieldWrapper(name);
            if (wrapper)
                wrapper.style.display = !isDisassemblyTab ? '' : 'none';
        });
    }

    function setActiveTab(tabElement) {
        allTabs.forEach((t) => t.classList.remove('active'));
        tabElement.classList.add('active');
        if (newManualLabel) newManualLabel.innerText = tabElement.innerText;
        if (stationTypeInput) stationTypeInput.value = tabElement.dataset.value;
    }

    const savedTabValue = localStorage.getItem(TABS_STORAGE_KEY);
    let activeTabOnLoad =
        tabsContainer.querySelector(`.tab[data-value="${savedTabValue}"]`) ||
        allTabs[0];

    setActiveTab(activeTabOnLoad);
    updateFilterVisibility(activeTabOnLoad.dataset.value);

    const table = window.tables.initializeManualsTable({
        handleFiltering: false,
    });

    const applyFilters = debounce(() => {
        const activeTab = tabsContainer.querySelector('.tab.active');
        const activeTabValue = activeTab.dataset.value;
        const currentFilters = [];

        currentFilters.push({
            field: activeTab.dataset.filter,
            type: '=',
            value: activeTabValue,
        });

        if (isArchivedField) {
            currentFilters.push({
                field: 'is_archived',
                type: '=',
                value: isArchivedField.value,
            });
        }

        const relevantFilterNames =
            activeTabValue === 'DD' ? deviceFilterNames : componentFilterNames;
        let value;
        relevantFilterNames.forEach((fieldName) => {
            value = null;
            const field = document.getElementById(`id_${fieldName}`);
            if (field.tagName === 'SELECT' && field._choices) {
                const choice = field._choices.getValue();
                if (!choice || choice.value === '') {
                    return;
                }
                value = choice.label;
            } else if (field && field.value) {
                value = field.value;
            }
            if (value) {
                currentFilters.push({
                    field: fieldName,
                    type: '=',
                    value: value,
                });
            }
        });
        table.setFilter(currentFilters);
    }, 300);

    allTabs.forEach((tab) => {
        tab.addEventListener('click', function () {
            if (this.classList.contains('active')) return;
            setActiveTab(this);
            localStorage.setItem(TABS_STORAGE_KEY, this.dataset.value);
            updateFilterVisibility(this.dataset.value);
            const newStationType = this.dataset.value;
            const newColumns = getManualsColumns(newStationType);
            table.setColumns(newColumns);
            applyFilters();
        });
    });
    if (isArchivedField) {
        isArchivedField.addEventListener('change', () => {
            applyFilters();
        });
    }
    if (filtersForm) {
        filtersForm.querySelectorAll('input, select').forEach((field) => {
            const eventType = field.tagName === 'SELECT' ? 'change' : 'input';
            field.addEventListener(eventType, applyFilters);
        });
    }
    const filter_btn = document.querySelector('.filter-btn');
    if (filter_btn) {
        window.tables.initializeTableFilter();
        const filtersContainer = document.querySelector(
            '#manualsFilters .form-fields',
        );
        if (
            filtersContainer &&
            window.utils &&
            typeof window.utils.hideOnClickOutside === 'function'
        ) {
            window.utils.hideOnClickOutside(filtersContainer, filter_btn);
        }
    }
}
